import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import Wordmark from "@/components/Wordmark";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(token ? "" : "This reset link is missing its token. Please request a new one.");

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please log in.");
      navigate("/login", { replace: true });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail);
      setError(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-12 bg-background">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 mb-8"><Wordmark iconClass="h-8 w-auto" textClass="text-lg" /></Link>
        <h1 className="font-display text-3xl font-semibold">Set a new password</h1>
        <p className="text-muted-foreground mt-1 text-sm">Choose a password you haven't used before.</p>
        {error && <div className="mt-5 p-3 rounded-xl bg-destructive/10 text-destructive text-sm" role="alert" data-testid="reset-error">{error}</div>}
        <form onSubmit={submit} className="space-y-4 mt-6">
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (min 6 chars)"
            className="w-full px-4 py-3 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="reset-password" />
          <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password"
            className="w-full px-4 py-3 rounded-xl border border-border bg-background outline-none focus:border-accent" data-testid="reset-confirm" />
          <button type="submit" disabled={busy || !token}
            className="w-full py-3 rounded-full bg-accent text-white font-medium hover:bg-accent/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-testid="reset-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update password
          </button>
        </form>
        <p className="text-center text-sm text-muted-foreground mt-6">
          <Link to="/forgot-password" className="text-accent font-medium" data-testid="reset-request-new">Request a new link</Link>
        </p>
      </div>
    </div>
  );
}
